/** Formas de las respuestas de la API que usa el frontend. */
export interface ContextOption {
  churchId: string;
  churchName: string;
  relations: string[];
}

export interface EffectivePermission {
  permission: string;
  scope: 'church' | 'department' | 'self' | 'platform';
  departmentId: string | null;
}

export interface Me {
  personId: string;
  isPlatformAdmin: boolean;
  /** Hijo sin cuenta en su propio aparato (lo ligaron papá o mamá). */
  isChild?: boolean;
  /** Equipo de multimedia de la iglesia (computadora de proyección vinculada). */
  isDevice?: boolean;
  activeChurchId: string | null;
  contexts: ContextOption[];
  permissions: EffectivePermission[];
}

export interface Person {
  id: string;
  givenName: string;
  /** Apellido completo ("paterno materno"). */
  familyName: string;
  paternalSurname: string;
  maternalSurname: string;
  birthDate: string | null;
  birthPlace: string | null;
  birthCity: string | null;
  birthState: string | null;
  birthCountry: string | null;
  address: string | null;
  baptized: boolean | null;
  gender: Gender | null;
  mobilePhone: string | null;
  homePhone: string | null;
}

export type Gender = 'femenino' | 'masculino';

export type MembershipStatus = 'pending' | 'active' | 'rejected' | 'ended';

export interface Membership {
  id: string;
  person_id: string;
  church_id: string;
  status: MembershipStatus;
  requested_at: string;
  started_at: string | null;
  ended_at: string | null;
  /** Motivo que escribió la iglesia al rechazar la solicitud. */
  end_reason?: string | null;
}

export interface Family {
  id: string;
  name: string;
  members: { personId: string; givenName: string; familyName: string; relationship: string; isManager: boolean; birthDate: string | null; gender: string | null; hasAccount?: boolean }[];
  pendingInvitations: { id: string; name: string; relationship: string }[];
}

export interface FamilyInvitation {
  id: string;
  familyId: string;
  familyName: string;
  invitedBy: string;
  relationship: string;
}

export interface Church {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
}

/** Mi cuenta: la Persona más el correo y si ya está confirmado. */
export interface MyAccount extends Person {
  email: string;
  emailVerified: boolean;
}

export interface Plan {
  code: string;
  name: string;
  description: string | null;
  monthlyPriceCents: number;
  currency: string;
  trialDays: number;
  capabilities: string[];
  memberLimit?: number | null;
  quarterlyPriceCents?: number | null;
  semiannualPriceCents?: number | null;
  annualPriceCents?: number | null;
}

export type BillingInterval = 'month' | 'quarter' | 'semester' | 'year';

/** Persona del directorio de la Iglesia (miembros y personas con rol). */
export interface ChurchMember {
  personId: string;
  /** Fundador según «Nuestra historia». */
  founder?: 'pastor' | 'miembro' | null;
  givenName: string;
  familyName: string;
  paternalSurname: string;
  maternalSurname: string;
  birthDate: string | null;
  birthPlace: string | null;
  birthCity: string | null;
  birthState: string | null;
  birthCountry: string | null;
  address: string | null;
  baptized: boolean | null;
  gender: Gender | null;
  mobilePhone: string | null;
  homePhone: string | null;
  email: string | null;
  membershipId: string | null;
  memberSince: string | null;
  roles: string[];
  /** Tiene cuenta para entrar a KEORA (si no, la capturó la iglesia). */
  hasAccount?: boolean;
}

/** Datos que le faltan a alguien del directorio (los capturados con lo mínimo los irán completando). */
export function missingData(m: ChurchMember): string[] {
  return [
    !m.birthDate && 'fecha de nacimiento',
    !m.mobilePhone && !m.homePhone && 'teléfono',
    !m.email && 'correo',
    !m.address && 'dirección',
  ].filter((x): x is string => Boolean(x));
}

export type MinistryKind = 'grupo' | 'raices' | 'servicio';
export interface PersonRef {
  personId: string;
  name: string;
}

/** Ministerio de la Iglesia (Departamento con tipo, líder, co-líderes y miembros). */
export interface Ministry {
  id: string;
  name: string;
  description: string | null;
  kind: MinistryKind;
  systemKey: string | null;
  /** Ministerio de vocación: publica vacantes en Mi Vocación. */
  vocation?: boolean;
  leader: PersonRef | null;
  coLeaders: PersonRef[];
  memberCount: number;
  canAssignCoLeader: boolean;
  canManageMembers: boolean;
  mine: boolean;
}

export interface Cell {
  id: string;
  name: string;
  representative: PersonRef | null;
  representatives: PersonRef[];
  members: PersonRef[];
}

export interface MinistryDetail extends Ministry {
  members: PersonRef[];
  automatic: boolean;
  candidates: PersonRef[];
  /** Células (Varones, Damas y Jóvenes); null en los demás ministerios. */
  cells: Cell[] | null;
  withoutCell: PersonRef[];
  /** Puestos del ministerio (Batería, Bajo…) y quiénes los ocupan. */
  positions: { id: string; name: string; variable: boolean; members: PersonRef[] }[];
  /** ¿Puede crear y asignar puestos? (administración, pastor o el líder). */
  canManagePositions: boolean;
  /** Multimedia: qué puede hacer cada integrante con los archivos (solo para quien lo decide). */
  mediaRights?: Record<string, MediaCan & { operate: boolean }> | null;
}

export interface MinistriesOverview {
  items: Ministry[];
  withoutGroup: PersonRef[];
  canAssignLeaders: boolean;
}

export type ProfileField = 'bio' | 'phone' | 'email' | 'birthday' | 'address' | 'ministries' | 'family';
export type Visibility = 'church' | 'leaders' | 'private';

/** Mi perfil: lo que escribo y quién ve cada dato. */
export interface MyProfile {
  bio: string | null;
  phone: string | null;
  visibility: Record<ProfileField, Visibility>;
}

/** Perfil de una persona tal como lo veo (solo lo que ella permite). */
export interface ProfileView {
  personId: string;
  name: string;
  /** Fundador según «Nuestra historia». */
  founder?: 'pastor' | 'miembro' | null;
  isMe: boolean;
  viewerLevel: 'church' | 'leaders' | 'self';
  /** Roles en la iglesia (pastor, admin, department_leader, member…). */
  roles?: string[];
  bio?: string | null;
  /** Celular. */
  phone?: string | null;
  homePhone?: string | null;
  email?: string | null;
  birthday?: { day: number; month: number } | null;
  address?: string | null;
  ministries?: { id: string; name: string }[];
  family?: { families: string[] };
}

/** Chat interno de un Ministerio. */
export interface ChatChannel {
  id: string;
  name: string;
  /** Chat de un ministerio o de una célula. */
  kind?: 'ministerio' | 'celula';
  lastMessage: { body: string; at: string; authorName: string } | null;
  unread: number;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  author: { personId: string; name: string };
  body: string;
  createdAt: string;
}

/** Familia dentro de la Iglesia, con el parentesco de cada integrante. */
export interface ChurchFamily {
  id: string;
  name: string;
  /** Viene de Mi familia: quiénes la forman lo decide la propia familia desde su perfil. */
  linked: boolean;
  members: ChurchFamilyMember[];
}

export interface ChurchFamilyMember {
  personId: string;
  givenName: string;
  familyName: string;
  relationship: string;
  fromHome: boolean;
  email?: string | null;
  birthDate?: string | null;
  membership?: 'pending' | 'active' | 'rejected' | 'ended' | null;
  membershipId?: string | null;
  classes?: string[];
  /** Cómo entra a KEORA: con su correo, con su celular ligado por papá o mamá, o sin cuenta (por Mi hogar). */
  access?: 'correo' | 'celular' | 'sin_cuenta';
  /** Familia de la iglesia de donde viene (allí fue hijo o hija), para el árbol familiar. */
  originFamilyId?: string | null;
}

/** Expediente del miembro en la Iglesia (lo llena la administración). */
export interface MemberRecord {
  conversionYear: number | null;
  conversionDate: string | null;
  previousChurch: string | null;
  baptismDate: string | null;
  spiritBaptismDate: string | null;
  ministryNotes: string | null;
  updatedAt: string | null;
  updatedBy: { personId: string; name: string } | null;
}

/** Iglesia vista desde el panel de la administración de KEORA. */
export interface AdminChurch {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
  timezone: string;
  createdAt: string;
  members: { active: number; pending: number };
  founder: { personId: string; name: string; email: string | null } | null;
  subscription: { planCode: string; planName: string; status: SubscriptionStatus; endsAt: string | null; provider: string | null } | null;
}

/** Plan visto por el Superadmin (incluye ocultos y archivados). */
export interface AdminPlan extends Plan {
  isPublic: boolean;
  archived: boolean;
  churches: number;
}

export type SubscriptionStatus = 'pending' | 'trialing' | 'trial_expired' | 'active' | 'grace' | 'past_due' | 'courtesy' | 'canceled';

export interface Subscription {
  plan: Plan;
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  daysLeft: number;
  canPay: boolean;
  /** Proveedores disponibles para pagar, p. ej. ['mercadopago', 'stripe']. */
  paymentProviders: string[];
  simulatedPayments: boolean;
  interval?: BillingInterval;
  intervals?: { interval: BillingInterval; totalCents: number }[];
  price?: { interval: BillingInterval; baseCents: number; addonsCents: number; discountPercent: number; discountCents: number; totalCents: number } | null;
  addons?: { id: string; description: string; monthlyPriceCents: number; quantity: number; extraMembers: number; moduleCode?: string | null }[];
  /** Módulos con costo que la iglesia contrata aparte (p. ej. cuestionarios con IA). */
  modules?: SubscriptionModule[];
  members?: { count: number; limit: number | null };
  discount?: { percent: number; until: string | null };
  courtesyUntil?: string | null;
  graceEndsAt?: string | null;
}

export interface SubscriptionModule {
  code: string;
  name: string;
  unit: string;
  available: boolean;
  monthlyPriceCents: number | null;
  monthlyLimit: number;
  contracted: boolean;
  used: number;
}

/** Estado del botón «Generar cuestionario automáticamente» de una clase. */
export interface AiQuizStatus {
  configured: boolean;
  contracted: boolean;
  inGoodStanding: boolean;
  monthlyLimit: number;
  used: number;
  hasContent: boolean;
  /** La clase ya tiene su cuestionario (uno por clase). */
  hasQuiz: boolean;
}

export interface CalendarEntry {
  id: string;
  /** 'calendar' = evento general; 'worship' = culto (se gestiona en Cultos). */
  sourceModule: string;
  sourceId: string | null;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string;
  status: 'scheduled' | 'canceled';
  /** Página del elemento (eventos de ministerio, reuniones, clases, jornadas de oración). */
  link?: string | null;
}

export interface ServiceType {
  id: string;
  name: string;
  weekday: number | null;
  startTime: string | null;
  durationMinutes: number;
  defaultLocation: string | null;
}

export type ServiceStatus = 'planned' | 'held' | 'canceled';

export interface WorshipService {
  id: string;
  title: string;
  serviceTypeId: string | null;
  notes: string | null;
  status: ServiceStatus;
  cancelReason: string | null;
  startsAt: string;
  endsAt: string;
  location: string | null;
}

export type AttendanceMethod = 'staff' | 'self' | 'location';

export interface Attendee {
  personId: string;
  name: string;
  method: AttendanceMethod;
  recordedAt: string;
}

export interface Headcount {
  total: number;
  visitors: number;
  recordedAt: string;
}

export interface ServiceAttendance {
  occasion: WorshipService;
  headcount: Headcount | null;
  attendees: Attendee[];
}

export interface PersonAttendance {
  occasion: WorshipService;
  method: AttendanceMethod;
  recordedAt: string;
}

export interface Department {
  id: string;
  name: string;
  description: string | null;
}

export type AssignmentStatus = 'requested' | 'accepted' | 'declined' | 'canceled';

export interface Assignment {
  id: string;
  personId: string;
  name: string;
  role: string;
  departmentId: string | null;
  departmentName: string | null;
  status: AssignmentStatus;
  declineReason: string | null;
  respondedAt: string | null;
}

export interface ServiceTeam {
  occasion: WorshipService;
  assignments: Assignment[];
  unavailable: { personId: string; name: string; note: string | null }[];
}

export interface MyAssignment extends Assignment {
  occasion: WorshipService;
}

export interface Unavailability {
  id: string;
  startsOn: string;
  endsOn: string;
  note: string | null;
}

export interface AppNotification {
  id: string;
  churchId: string | null;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
  readAt: string | null;
  /** Aviso de grupo que ya atendió alguien: deja de contar como sin leer. */
  resolvedAt?: string | null;
  /** Nombre de quien lo atendió (null si fue automático, p. ej. un pago). */
  resolvedBy?: string | null;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  publishedBy: { personId: string; name: string };
  publishedAt: string;
}

export interface PrayerRequest {
  id: string;
  body: string;
  visibility: 'church' | 'pastoral' | 'ministry';
  anonymous: boolean;
  status: 'open' | 'answered' | 'closed';
  answerNote: string | null;
  createdAt: string;
  author: { personId: string; name: string } | null;
  isMine: boolean;
  prayedCount: number;
  prayedToday: boolean;
}

export type FormFieldType = 'text' | 'textarea' | 'number' | 'date' | 'choice' | 'multi' | 'checkbox';
export type FormAnswer = string | number | boolean | string[];

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  options?: string[];
}

export interface ChurchForm {
  id: string;
  title: string;
  description: string | null;
  fields: FormField[];
  onePerPerson: boolean;
  requiresApproval: boolean;
  status: 'draft' | 'open' | 'closed';
  createdAt: string;
  responseCount: number;
  answered?: boolean;
}

export interface FormResponse {
  id: string;
  person: { personId: string; name: string };
  answers: Record<string, FormAnswer>;
  submittedAt: string;
  approval: { requestId: string; status: ApprovalStatus; note: string | null } | null;
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export interface ApprovalRequest {
  id: string;
  sourceModule: string;
  sourceId: string;
  kind: string;
  title: string;
  summary: string | null;
  link: string | null;
  requestedBy: { personId: string; name: string };
  status: ApprovalStatus;
  decidedBy: { personId: string; name: string } | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export type FinanceAccountKind = 'cash' | 'bank' | 'fund';
export type FinanceMovementKind = 'income' | 'expense' | 'transfer_in' | 'transfer_out';

export interface FinanceAccount {
  id: string;
  name: string;
  kind: FinanceAccountKind;
  currency: string;
  balanceCents: number;
  archived: boolean;
}

export interface FinanceCategory {
  id: string;
  kind: 'income' | 'expense';
  name: string;
}

export interface FinanceOverview {
  accounts: FinanceAccount[];
  categories: FinanceCategory[];
  closedThrough: string | null;
  today: string;
}

export interface FinanceMovement {
  id: string;
  account: { id: string; name: string; currency: string };
  kind: FinanceMovementKind;
  amountCents: number;
  occurredOn: string;
  category: FinanceCategory | null;
  description: string | null;
  person: { personId: string; name: string } | null;
  transferId: string | null;
  reversalOf: string | null;
  reason: string | null;
  reversed: boolean;
  recordedBy: { personId: string; name: string };
  recordedAt: string;
  /** Comprobantes (PDF o imagen). */
  files: FinanceMovementFile[];
}

export interface FinanceMovementFile {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
}

export interface FinanceDashboard {
  from: string;
  to: string;
  granularity: 'day' | 'week' | 'month';
  series: { start: string; incomeCents: number; expenseCents: number }[];
  byCategory: { category: FinanceCategory; totalCents: number }[];
  incomeCents: number;
  expenseCents: number;
  previous: { from: string; to: string; incomeCents: number; expenseCents: number };
}

export interface FinanceSummary {
  from: string;
  to: string;
  income: { category: FinanceCategory; totalCents: number }[];
  expense: { category: FinanceCategory; totalCents: number }[];
  incomeCents: number;
  expenseCents: number;
}

export interface MyContributions {
  items: { id: string; occurredOn: string; amountCents: number; currency: string; category: string; family: string | null }[];
  totalCents: number;
  currency: string | null;
}

export interface InventoryLocation {
  id: string;
  name: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  sku: string | null;
  unit: string;
  kind: 'consumable' | 'asset';
  minQuantity: number | null;
  total: number;
  byLocation: { locationId: string; quantity: number }[];
  low: boolean;
}

export interface InventoryMovement {
  id: string;
  item: { id: string; name: string; unit: string };
  location: InventoryLocation;
  kind: 'entry' | 'exit' | 'consumption' | 'return' | 'adjustment' | 'transfer_in' | 'transfer_out';
  quantity: number;
  note: string | null;
  transferId: string | null;
  source: { module: string; id: string } | null;
  recordedBy: { personId: string; name: string };
  recordedAt: string;
}

export type PaymentMethod = 'cash' | 'card' | 'transfer' | 'wallet';

export interface Product {
  id: string;
  name: string;
  priceCents: number;
  currency: string;
  active: boolean;
  inventory: { itemId: string; locationId: string; quantity: number } | null;
}

export interface PosSession {
  id: string;
  register: { id: string; name: string };
  openedBy: { personId: string; name: string };
  openedAt: string;
  closedAt: string | null;
  totals: {
    openingFloatCents: number;
    sales: Record<PaymentMethod, number>;
    refunds: Record<PaymentMethod, number>;
    expensesCents: number;
    withdrawalsCents: number;
    orders: number;
  };
  expectedCashCents: number;
  countedCashCents: number | null;
  differenceCents: number | null;
  cashEvents: { kind: 'expense' | 'withdrawal'; amountCents: number; note: string; recordedAt: string }[];
}

export interface Order {
  id: string;
  sessionId: string;
  number: number;
  customerName: string | null;
  paymentMethod: PaymentMethod;
  totalCents: number;
  status: 'paid' | 'refunded';
  fulfillment: 'pending' | 'preparing' | 'ready' | 'delivered';
  items: { productId: string; name: string; unitPriceCents: number; quantity: number }[];
  createdBy: { personId: string; name: string };
  createdAt: string;
  refundReason: string | null;
}

export type PieceKind = 'article' | 'sermon' | 'study';
export type PieceStatus = 'draft' | 'in_review' | 'approved' | 'archived';

export interface ContentPiece {
  id: string;
  kind: PieceKind;
  status: PieceStatus;
  title: string;
  summary: string | null;
  currentRevision: number;
  approvedRevision: number | null;
  createdBy: PersonRef;
  updatedAt: string;
}

export interface ContentRevision {
  number: number;
  title: string;
  summary: string | null;
  body: string;
  preacher: string | null;
  passage: string | null;
  preachedOn: string | null;
  createdBy: PersonRef;
  createdAt: string;
}

export interface ContentPieceDetail extends ContentPiece {
  revision: ContentRevision;
  history: { number: number; title: string; createdBy: PersonRef; createdAt: string }[];
  events: { action: 'created' | 'edited' | 'submitted' | 'approved' | 'returned' | 'archived' | 'restored'; revision: number; note: string | null; actor: PersonRef; at: string }[];
}

export type Channel = 'church' | 'public';

export interface PublicationItem {
  id: string;
  pieceId: string;
  revision: number;
  kind: PieceKind;
  title: string;
  summary: string | null;
  channel: Channel;
  publishAt: string;
  state: 'scheduled' | 'published' | 'withdrawn';
  videoUrl?: string | null;
  series?: { id: string; title: string } | null;
  preacher?: string | null;
  preachedOn?: string | null;
}

export interface FollowUp {
  questions: string[];
  keyVerse: string | null;
  groupGuide: string | null;
  prayer: string | null;
  challenge: string | null;
}

export interface PublicationDetail extends PublicationItem {
  body: string;
  followUp?: FollowUp | null;
  preacher: string | null;
  passage: string | null;
  preachedOn: string | null;
}

export interface ManagedPublication extends PublicationItem {
  withdrawnAt: string | null;
  withdrawReason: string | null;
  createdBy: PersonRef;
}

export interface BibleReference {
  book: string;
  chapter: number;
  verseStart: number | null;
  verseEnd: number | null;
}

export interface BibleTranslation {
  code: string;
  name: string;
  language: string;
  license: string;
}

export interface BiblePassage {
  reference: BibleReference;
  label: string;
  translation: BibleTranslation | null;
  verses: { verse: number; text: string }[];
  previous: string | null;
  next: string | null;
}

export interface BibleBook {
  code: string;
  name: string;
  testament: 'OT' | 'NT';
  chapters: number;
}

export interface SavedPassage {
  id: string;
  reference: BibleReference;
  label: string;
  note: string | null;
  createdAt: string;
  category?: 'promesa' | 'ensenanza' | 'oracion' | 'importante' | 'estudio' | null;
  share?: { churchId: string; scope: 'familia' | 'celula' | 'ministerio'; target: string | null } | null;
}

export interface ReadingPlan {
  id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  totalDays: number;
  enrolled: number | null;
  mine: { startedOn: string; status: 'active' | 'finished' | 'left'; today: number; completed: number[] } | null;
}

export interface ReadingPlanDetail extends ReadingPlan {
  days: { day: number; title: string | null; passages: string[]; reflection: string | null }[];
}

export interface TodayReading {
  planId: string;
  planTitle: string;
  day: number;
  totalDays: number;
  passages: string[];
}

export type MediaKind = 'image' | 'audio' | 'video' | 'document';

export interface MediaResource {
  id: string;
  kind: MediaKind;
  name: string;
  mime: string;
  sizeBytes: number;
  uploadedBy: { personId: string; name: string };
  createdAt: string;
  archived: boolean;
  /** Carpeta en Multimedia (p. ej. la de la Predicación de un culto). */
  folder?: string | null;
  pages?: number | null;
}

/** Lo que puede hacer quien consulta con los archivos: subir, modificar (renombrar) o eliminar (archivar). */
export interface MediaCan {
  upload: boolean;
  edit: boolean;
  delete: boolean;
}

export interface MediaLibrary {
  items: MediaResource[];
  usedBytes: number;
  maxUploadBytes: number;
  can?: MediaCan;
}

export type PresentationItemKind = 'lyrics' | 'text' | 'scripture' | 'media';
export type Audience = 'stage' | 'all';

export interface PresentationSlide {
  text: string | null;
  caption: string | null;
  /** Parte del canto ("Coro"…), si la letra la nombra. */
  label?: string | null;
}

export interface PresentationResourceRef {
  id: string;
  kind: MediaKind;
  mime: string;
  name: string;
  /** Páginas de un PDF. */
  pages?: number | null;
}

export interface PresentationItemInput {
  kind: PresentationItemKind;
  title: string;
  body: string | null;
  passage: string | null;
  resourceId: string | null;
  stageNotes: string | null;
  /** Solo cantos: orden de las partes nombradas; vacío = como está escrita. */
  arrangement?: string[] | null;
  /** Fondo KEORA del elemento (si no trae imagen propia). */
  backgroundPreset?: string | null;
  /** Archivo de la carpeta de la computadora de proyección (no se sube al servidor). */
  localFile?: string | null;
  /** Video de YouTube (enlace al guardar; su id al leer): se proyecta desde YouTube, sin descargarlo. */
  youtube?: string | null;
  /** Solo textos de la Biblia: la versión (p. ej. RV1909); sin ella, la de siempre. */
  translation?: string | null;
  /** Fuente de video (cámara o capturadora) de la computadora de proyección, por su id. */
  camera?: string | null;
}

export interface PresentationItem extends PresentationItemInput {
  position: number;
  resource: PresentationResourceRef | null;
  slides: PresentationSlide[];
}

export interface PresentationSummary {
  id: string;
  title: string;
  service: { id: string; title: string; startsAt: string } | null;
  items: number;
  updatedAt: string;
  archived: boolean;
}

export interface PresentationDetail extends PresentationSummary {
  itemsList: PresentationItem[];
}

export interface LiveBackground {
  resource: PresentationResourceRef | null;
  localFile: { name: string; kind: 'image' | 'video' | 'audio' } | null;
  /** Fuente de video (cámara) de la computadora de proyección, por su id. */
  camera?: string | null;
}

export interface RenderedSlide {
  item: number;
  slide: number;
  kind: PresentationItemKind;
  title: string;
  text: string | null;
  caption: string | null;
  resource: PresentationResourceRef | null;
  /** Página del PDF que se proyecta. */
  page?: number | null;
  label?: string | null;
  backgroundPreset?: string | null;
  /** Archivo de la computadora de proyección: solo lo muestra la app KEORA Multimedia. */
  localFile?: { name: string; kind: 'image' | 'video' | 'audio' } | null;
  /** Id de un video de YouTube. */
  youtube?: string | null;
  /** Fuente de video (cámara) de la computadora de proyección, por su id. */
  camera?: string | null;
}

export interface SlideCursor {
  item: number;
  slide: number;
}

export type ThemeFont = 'clasica' | 'elegante' | 'fuerte' | 'moderna' | 'manuscrita';
export type ThemeSize = 'chica' | 'normal' | 'grande' | 'enorme';
export type ThemePosition = 'arriba' | 'centro' | 'abajo';
export type ThemeAlign = 'izquierda' | 'centro' | 'derecha';

/** Tema de las pantallas de la iglesia (uno para todas las presentaciones). */
export interface ScreenTheme {
  font: ThemeFont;
  size: ThemeSize;
  textColor: string;
  bgColor: string;
  uppercase: boolean;
  /** Dónde va el texto (si falta, al centro). */
  position?: ThemePosition;
  align?: ThemeAlign;
  background: PresentationResourceRef | null;
  /** Fondo KEORA (si no hay imagen de fondo). */
  backgroundPreset?: string | null;
}

export interface ThemeDetail extends ScreenTheme {
  logo: PresentationResourceRef | null;
  siteLogoId: string | null;
  churchName: string;
}

export interface OutputState {
  version: string;
  serverTime: string;
  title: string;
  blackout: boolean;
  live: RenderedSlide | null;
  /** «Limpiar»: las pantallas quitan el texto y dejan el fondo. */
  textHidden?: boolean;
  /** Anuncio que corre en el listón rojo de arriba. */
  notice: string | null;
  /** Reproducción del video en vivo: todas las salidas obedecen el último comando (por su secuencia). */
  playback?: { seq: number; playing: boolean; position: number; at: string | null; volume: number; muted: boolean };
  /** Cuándo empezó a pasar el listón y cuántas veces pasa (null = siempre). */
  noticeStartedAt?: string | null;
  noticeRepeat?: number | null;
  /** Mensaje solo para los músicos (pantalla del escenario y consola). */
  stageNotice?: string | null;
  stageNoticeStartedAt?: string | null;
  stageNoticeRepeat?: number | null;
  timer: { endsAt: string; label: string | null } | null;
  theme: ScreenTheme;
  /** Con el botón Logo: lo que se proyecta en lugar de la diapositiva. */
  logo: { resourceId: string | null; name: string } | null;
  /** Conteo a pantalla completa (antes del culto). */
  countdown?: boolean;
  /** Logo y nombre de la iglesia (para cuando el conteo llega a cero). */
  brand?: { resourceId: string | null; name: string };
  /** Fondo en vivo (video o imagen) detrás de la letra de cantos y textos, hasta quitarlo. */
  liveBackground?: LiveBackground | null;
  next?: RenderedSlide | null;
  notes?: string | null;
}

export interface Announcement {
  id: string;
  text: string;
}

export interface OperatorState extends OutputState {
  preview: RenderedSlide | null;
  previewCursor: SlideCursor | null;
  liveCursor: SlideCursor | null;
  noticeAudience: Audience;
  timerAudience: Audience;
}

export type AutomationKind = 'day_reminder' | 'service_reminder' | 'reading_reminder';

export interface AutomationRule {
  kind: AutomationKind;
  enabled: boolean;
  config: { hoursBefore?: number; hour?: number };
  updatedAt: string | null;
}

export interface AutomationRun {
  id: string;
  kind: AutomationKind;
  trigger: 'schedule' | 'manual';
  startedAt: string;
  outcome: 'succeeded' | 'failed';
  notified: number;
  error: string | null;
}

export type ReportIndicator = 'attendance_by_service' | 'membership' | 'finance_by_month' | 'service_participation';
export type ReportPeriod = 'last_4_weeks' | 'last_3_months' | 'this_year' | 'custom';

export interface ReportDefinition {
  id: string;
  name: string;
  indicators: ReportIndicator[];
  period: ReportPeriod;
  from: string | null;
  to: string | null;
  updatedAt: string;
}

export interface ReportingOverview {
  indicators: { key: ReportIndicator; label: string; available: boolean }[];
  definitions: ReportDefinition[];
  canManage: boolean;
}

export type ReportSection =
  | { indicator: 'attendance_by_service'; label: string; status: 'ok'; rows: { serviceId: string; title: string; startsAt: string; headcount: number | null; visitors: number | null; recorded: number }[]; summary: { services: number; counted: number; averageHeadcount: number | null } }
  | { indicator: 'membership'; label: string; status: 'ok'; data: { active: number; joined: number; ended: number } }
  | { indicator: 'finance_by_month'; label: string; status: 'ok'; rows: { month: string; currency: string; incomeCents: number; expenseCents: number }[] }
  | { indicator: 'service_participation'; label: string; status: 'ok'; data: { services: number; accepted: number; declined: number; pending: number } }
  | { indicator: ReportIndicator; label: string; status: 'forbidden' };

export interface Report {
  period: { preset: ReportPeriod; from: string; to: string };
  timezone: string;
  generatedAt: string;
  sections: ReportSection[];
}

export type CareKind = 'meal' | 'nap' | 'diaper' | 'note' | 'incident' | 'call_guardian';

export interface NurseryRoom {
  id: string;
  name: string;
  minAgeMonths: number | null;
  maxAgeMonths: number | null;
  capacity: number | null;
}

export interface CareEvent {
  id: string;
  kind: CareKind;
  note: string | null;
  recordedBy: string;
  recordedAt: string;
}

export interface MyNurseryChild {
  personId: string;
  /** Número de identificador en la guardería (null si aún no tiene ficha). */
  number?: number | null;
  name: string;
  birthDate: string | null;
  ageMonths: number | null;
  registered: boolean;
  allergies: string | null;
  instructions: string | null;
  medical: string | null;
  contactPhone: string | null;
  diaper: boolean | null;
  photoConsent: boolean | null;
  pickups: { id: string; name: string; relationship: string }[];
  stay: {
    id: string;
    room: { id: string; name: string };
    code: string;
    checkedInAt: string;
    droppedOffBy: string;
    events: CareEvent[];
    cameras: { id: string; name: string }[];
  } | null;
}

export interface MyNursery {
  children: MyNurseryChild[];
  history: { id: string; childName: string; roomName: string; checkedInAt: string; checkedOutAt: string; pickedUpBy: string }[];
  rooms: NurseryRoom[];
  camerasEnabled: boolean;
}

export interface NurseryBoard {
  rooms: (NurseryRoom & {
    stays: { id: string; child: { personId: string; number?: number | null; name: string; ageMonths: number | null }; allergies: string | null; instructions: string | null; medical?: string | null; contactPhone?: string | null; diaper?: boolean | null; photoConsent?: boolean | null; droppedOffBy: string; checkedInAt: string; events: CareEvent[] }[];
    cameras: { id: string; name: string }[];
  })[];
  children: { personId: string; number?: number; name: string; ageMonths: number | null; allergies: string | null; guardians: { personId: string; name: string }[] }[];
  camerasEnabled: boolean;
  canManage: boolean;
  canRelease: boolean;
}

export interface NurseryTeam {
  role: 'leader' | 'co_leader' | 'member' | null;
  onDutyToday: boolean;
  canManageRoster: boolean;
  chatId: string | null;
  myShifts: { serviceId: string; title: string; startsAt: string }[];
}

export interface NurseryRoster {
  canManage: boolean;
  types: { id: string; name: string; weekday: number | null; selected: boolean }[];
  services: { id: string; title: string; startsAt: string; people: { personId: string; name: string }[] }[];
  team: { personId: string; name: string }[];
}

export interface CameraLogEntry {
  id: string;
  person: string;
  camera: string;
  basis: 'guardian' | 'staff';
  grantedAt: string;
  expiresAt: string;
  views: number;
}

// ── Eventos ──
export type EventStatus = 'pending' | 'approved' | 'rejected' | 'canceled';
export type InvitationStatus = 'pending' | 'accepted' | 'declined';

export interface ChurchEventSummary {
  id: string;
  scope: 'general' | 'ministry';
  ministry: { id: string; name: string } | null;
  title: string;
  placeName: string | null;
  startsAt: string;
  endsAt: string;
  status: EventStatus;
  /** Sale en la página pública de la iglesia. */
  isPublic: boolean;
  canDecide: boolean;
  pendingInvitations: { id: string; name: string }[];
}

export interface MyEventTask {
  eventId: string;
  eventTitle: string;
  startsAt: string;
  taskId: string;
  title: string;
  done: boolean;
}

export interface EventConflict {
  kind: 'event' | 'worship' | 'calendar' | 'other';
  title: string;
  startsAt: string;
  endsAt: string;
  place: string | null;
  samePlace: boolean;
  pending: boolean;
  link: string | null;
}

export interface ChurchEventDetail extends ChurchEventSummary {
  conflicts?: EventConflict[];
  purpose: string | null;
  address: string | null;
  mapsUrl: string | null;
  createdBy: PersonRef;
  decision: { by: PersonRef | null; at: string | null; note: string | null } | null;
  invitedMinistries: { id: string; name: string; status: InvitationStatus; canRespond: boolean }[];
  people: PersonRef[];
  tasks: { id: string; title: string; done: boolean; doneBy: PersonRef | null; assignees: PersonRef[]; canToggle: boolean }[];
  canManage: boolean;
  canCancel: boolean;
  publicToken: string | null;
  rsvps: { count: number; guests: number; items: { name: string; contact: string | null; guests: number; at: string }[] } | null;
}

export interface EventOptions {
  canCreateGeneral: boolean;
  generalNeedsApproval: boolean;
  ministries: { id: string; name: string; needsApproval: boolean }[];
  allMinistries: { id: string; name: string }[];
  people: PersonRef[];
}

export interface PublicEvent {
  title: string;
  purpose: string | null;
  placeName: string | null;
  address: string | null;
  mapsUrl: string | null;
  startsAt: string;
  endsAt: string;
  churchName: string;
}

// ── Reuniones de ministerio y célula ──
export interface MeetingSummary {
  id: string;
  ministry: { id: string; name: string };
  withMinistries: { id: string; name: string }[];
  cell: { id: string; name: string } | null;
  topic: string;
  startsAt: string;
  endsAt: string;
  placeName: string | null;
  hasFood: boolean;
  canceled: boolean;
  /** Sale en la página pública de la iglesia. */
  isPublic: boolean;
}

export interface MinistryMeetings {
  items: MeetingSummary[];
  canCreateForMinistry: boolean;
  cellsICanManage: { id: string; name: string }[];
  candidates: PersonRef[];
  ministries: { id: string; name: string }[];
}

export interface MeetingDetail extends MeetingSummary {
  speaker: { personId: string | null; name: string } | null;
  host: PersonRef | null;
  address: string | null;
  mapsUrl: string | null;
  foodNotes: string | null;
  notes: string | null;
  createdBy: PersonRef;
  food: { id: string; item: string; person: PersonRef | null; canClaim: boolean; canRelease: boolean }[];
  prayers: { id: string; author: PersonRef; body: string; createdAt: string; canDelete: boolean }[];
  participants: PersonRef[];
  canManage: boolean;
  candidates: PersonRef[];
  ministryOptions: { id: string; name: string }[];
}

// ── Salones de Raíces ──
export interface ClassroomSummary {
  id: string;
  ministry: { id: string; name: string; kids: boolean };
  name: string;
  description: string | null;
  minAge: number | null;
  maxAge: number | null;
  teachers: PersonRef[];
  studentCount: number;
  nextSession: { id: string; startsAt: string; topic: string | null } | null;
  role: 'manager' | 'teacher' | 'student';
  /** La clase que está en vivo ahora, si hay. */
  liveSessionId?: string | null;
  /** Alumno: su lugar en la tabla de posiciones del salón. */
  standing?: { position: number; of: number; points: number } | null;
}

export interface DashboardSession {
  id: string;
  startsAt: string;
  endsAt: string;
  topic: string | null;
  canceled: boolean;
  attendanceTaken: boolean;
  present: number;
  hasQuiz: boolean;
  hasNotes: boolean;
  badVerses?: boolean;
}

/** Panel del maestro de un salón. */
export interface TeacherDashboard {
  room: { id: string; name: string; kids: boolean };
  today: DashboardSession | null;
  sessions: DashboardSession[];
  students: {
    person: PersonRef;
    attendance: { present: number; total: number };
    quizPoints: number;
    manualPoints: number;
    points: number;
    average: number | null;
    pendingQuizzes: number;
  }[];
  summary: { monthClasses: number; monthPresent: number; monthAbsent: number; toGrade: number; drafts: number };
}

export interface PointsEntry {
  id: string;
  person: PersonRef;
  points: number;
  reason: string;
  sessionId: string | null;
  by: PersonRef;
  at: string;
}

/** Subrayado/nota de la lectura de la clase, o imagen/enlace del maestro. */
export interface ClassAnnotation {
  id: string;
  teacher: boolean;
  mine: boolean;
  kind: 'highlight' | 'image' | 'link';
  page: number;
  start: number | null;
  end: number | null;
  quote: string | null;
  color: 'amarillo' | 'verde' | 'azul' | 'rosa' | null;
  note: string | null;
  url: string | null;
  documentId: string | null;
  createdAt: string;
}

export interface ClassLiveState {
  live: boolean;
  page: number;
  floor: PersonRef | null;
  shown: ClassAnnotation | null;
  hands: { person: PersonRef; at: string }[] | null;
  handsCount: number;
  myHand: boolean;
  pick?: PersonRef | null;
  teams?: PersonRef[][] | null;
  timer?: { endsAt: string; seconds: number } | null;
  now?: string;
  poll?: { id: string; question: string; options: string[]; open: boolean; counts: number[]; total: number; myAnswer: number | null } | null;
  /** 📷 Alumno con cámara aceptada. */
  camera?: PersonRef | null;
  /** Solo quien enseña: quiénes piden cámara. */
  cameraRequests?: PersonRef[] | null;
  myCameraRequest?: boolean;
  /** Solo quien enseña: alumnos conectados ahora mismo. */
  online?: PersonRef[] | null;
}

export interface Standings {
  period: 'month' | 'total';
  kids: boolean;
  entries: { position: number; name: string; points: number; answered: number; me: boolean }[];
}

export interface MyGrades {
  items: { quizId: string; title: string; kind: string; classroom: { id: string; name: string }; session: { id: string; startsAt: string; topic: string | null } | null; score: number | null; maxScore: number | null; submittedAt: string }[];
  average: number | null;
  hideName: boolean;
}

export interface ClassSession {
  id: string;
  startsAt: string;
  endsAt: string;
  topic: { id: string; title: string } | null;
  verses: string | null;
  /** Citas que no se reconocen (para corregirlas). */
  invalidVerses?: string[];
  notes: string | null;
  homework: string | null;
  canceled: boolean;
}

export interface QuizQuestion {
  id: string;
  text: string;
  type: 'choice' | 'text';
  options: string[];
  correct: number | null;
  points: number;
}

export interface Quiz {
  id: string;
  sessionId: string | null;
  kind: 'cuestionario' | 'examen';
  title: string;
  instructions: string | null;
  published: boolean;
  closesAt: string | null;
  open: boolean;
  /** Quien enseña puede borrarlo (el de una clase, solo la administración y el pastor). */
  canDelete?: boolean;
  questions: QuizQuestion[];
  mySubmission: { answers: (number | string | null)[]; score: number | null; maxScore: number | null; submittedAt: string; openGrades?: Record<string, number> } | null;
  results: { person: PersonRef; score: number | null; maxScore: number | null; submittedAt: string; answers: (number | string | null)[]; openGrades?: Record<string, number> }[] | null;
}

export interface ClassroomDetail extends ClassroomSummary {
  canManage: boolean;
  canTeach: boolean;
  ministries: { id: string; name: string }[];
  people: PersonRef[];
  students: PersonRef[];
  topics: { id: string; position: number; title: string; description: string | null }[];
  sessions: ClassSession[];
  posts: { id: string; author: PersonRef; body: string; createdAt: string }[];
  documents: { id: string; title: string; mime: string; sessionId: string | null; createdAt: string }[];
  quizzes: Quiz[];
  candidates: PersonRef[];
  ministryOptions: { id: string; name: string }[];
}

export interface ClassSessionDetail extends ClassSession {
  classroom: { id: string; name: string };
  canTeach: boolean;
  attendance: { person: PersonRef; present: boolean | null; participation: number | null; online?: boolean }[];
  documents: { id: string; title: string; mime: string }[];
  quizzes: Quiz[];
}

// ── Ministerio de Oración: jornadas ──
export interface PrayerDay {
  id: string;
  title: string | null;
  startsAt: string;
  endsAt: string;
  mode: 'presencial' | 'virtual';
  placeName: string | null;
  address: string | null;
  mapsUrl: string | null;
  meetingUrl: string | null;
  leader: PersonRef | null;
  notes: string | null;
  canceled: boolean;
  requestCount: number;
  /** A quién se convoca: el ministerio de Oración, solo algunos o toda la iglesia. */
  audience: 'ministerio' | 'elegidos' | 'iglesia';
  convoked: PersonRef[];
  chosen: string[];
}

export interface PrayerDayDetail extends PrayerDay {
  canManage: boolean;
  requests: PrayerRequest[] | null;
}

export interface PrayerOverview {
  items: PrayerDay[];
  team: { member: boolean; leader: boolean };
  people: PersonRef[];
}

// ── Visitantes ──
export type VisitorStage = 'primera_visita' | 'contactado' | 'segunda_visita' | 'integrandose' | 'raices' | 'grupo' | 'miembro' | 'no_continua';
export interface Visitor {
  id: string;
  givenName: string;
  familyName: string;
  phone: string | null;
  email: string | null;
  invitedBy: string | null;
  firstVisit: string;
  interests: string[];
  prayerRequest: string | null;
  stage: VisitorStage;
  follower: PersonRef | null;
  source: 'recepcion' | 'formulario';
  createdAt: string;
  lastContactAt: string | null;
  /** Datos de registro (opcionales) para reutilizarlos si se hace miembro. */
  details?: Record<string, unknown>;
  /** Culto en el que llegó (registrado por Bienvenida). */
  serviceId?: string | null;
}
export interface VisitorDetail extends Visitor {
  notes: { id: string; author: PersonRef; kind: string; body: string; createdAt: string }[];
  team: PersonRef[];
  canDelete: boolean;
}

// ── Devocional diario ──
export interface DailyToday {
  day: string;
  verse: { label: string; text: string; translation: string | null } | null;
  prayer: string | null;
  reflection: string | null;
  prepared: boolean;
  hasAudio?: boolean;
  canManage: boolean;
  /** «Amén»: si ya lo dije hoy, cuántos van y si puedo ver quiénes (pastor y administración). */
  amen?: { mine: boolean; count: number; canSeeList: boolean };
}

// ── Cantos (Alabanza) ──
export interface Song {
  id: string;
  title: string;
  author: string | null;
  lyrics: string;
  key: string | null;
  bpm: number | null;
  /** Versión o arreglo. */
  version?: string | null;
  notes: string | null;
  links: string[];
  archived: boolean;
  updatedAt: string;
}
export interface SetlistSong extends Song {
  position: number;
  serviceKey: string | null;
  /** Nota de este canto en este culto (p. ej. «Alabanza», «Adoración»). */
  serviceNotes?: string | null;
}

// ── Sitio público de la iglesia ──
export interface ChurchSchedule {
  label: string;
  day: string;
  time: string;
}
export interface ChurchProfile {
  tagline: string | null;
  about: string | null;
  denomination: string | null;
  foundedYear: number | null;
  pastorName: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  mapsUrl: string | null;
  /** Marcador en el mapa (los dos o ninguno). */
  latitude: number | null;
  longitude: number | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  youtubeUrl: string | null;
  helpLine: string | null;
  primaryColor: string | null;
  schedules: ChurchSchedule[];
  listed: boolean;
}
export interface HeroSlide {
  id: string;
  mediaId: string;
  title: string | null;
  description: string | null;
  buttonText: string | null;
  buttonUrl: string | null;
  startsOn: string | null;
  endsOn: string | null;
  priority: number;
  active: boolean;
}
export interface PublicSite extends Omit<ChurchProfile, 'listed'> {
  /** Código de la iglesia (para «Solicitar unirme»). */
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  slides: { id: string; imageUrl: string; title: string | null; description: string | null; buttonText: string | null; buttonUrl: string | null }[];
}
export interface ChurchSearchResult {
  slug: string;
  name: string;
  tagline: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  logoUrl: string | null;
  /** Solo en «cerca de mí». */
  distanceKm?: number;
}
export interface SermonSeries {
  id: string;
  title: string;
  description: string | null;
  archived: boolean;
  pieces: number;
}

/** Computadora vinculada como equipo de multimedia de la iglesia. */
export interface MultimediaDevice {
  id: string;
  name: string;
  linkedAt: string;
  lastSeen: string;
  linkedBy: string;
}

/** Lo que muestra el reproductor «Iglesia en vivo»: el directo, los videos en bucle o nada (se oculta). */
export interface LiveState {
  status: 'live' | 'loop' | 'off';
  kind: 'youtube' | 'facebook' | 'facebook-page' | null;
  videoId: string | null;
  url: string | null;
  since: string | null;
  videos: string[];
}
export interface LiveSettings {
  youtubeChannelUrl: string | null;
  youtubeChannelId: string | null;
  facebookPageUrl: string | null;
  loopVideos: string[];
  manual: { url: string | null; until: string } | null;
  canConfigure: boolean;
  state: LiveState;
}
